import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useHospitalSelection } from '../hooks/useHospitalSelection'
import { parseMonthToValue } from '../utils/dataUtils'
import { MonthInfo } from '../utils/tmbUtils'

interface TmbSliderContextType {
    globalMonthList: MonthInfo[]
    loading: boolean
}

const TmbSliderContext = createContext<TmbSliderContextType>({
    globalMonthList: [],
    loading: true
})

export const useTmbSlider = () => useContext(TmbSliderContext)

const TMB_DATE_APIS = [
    '/api/tmb/patient_surveys',
    '/api/tmb/patient_demographics',
    '/api/tmb/huddle_participation_monthly',
    '/api/tmb/General_Patient_comments',
    '/api/tmb/agreement_single'
]

const DATE_COLUMN_CANDIDATES = ['month', 'survey_month', 'time_period']

function extractDatesFromRows(rows: any[], columns: string[]): number[] {
    const dateColIdx = columns.findIndex(c =>
        DATE_COLUMN_CANDIDATES.some(candidate =>
            c.toLowerCase().replace(/[_\s]/g, '') === candidate.toLowerCase().replace(/[_\s]/g, '')
        )
    )
    if (dateColIdx === -1) return []

    const values: number[] = []
    for (const row of rows) {
        if (!row || !row[dateColIdx]) continue
        const val = parseMonthToValue(row[dateColIdx])
        if (val > 0) values.push(val)
    }
    return values
}

function buildMonthList(minVal: number, maxVal: number): MonthInfo[] {
    if (!minVal || !maxVal || minVal > maxVal) return []

    const list: MonthInfo[] = []
    let year = Math.floor(minVal / 100)
    let month = minVal % 100

    while (year * 100 + month <= maxVal) {
        const val = year * 100 + month
        const date = new Date(year, month - 1, 1)
        list.push({
            label: date.toLocaleString('en-US', { month: 'short', year: 'numeric' }),
            value: val,
            date
        })
        month++
        if (month > 12) { month = 1; year++ }
    }

    return list
}

export const TmbSliderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { selectedHospitalIds } = useHospitalSelection()
    const [globalMonthList, setGlobalMonthList] = useState<MonthInfo[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!selectedHospitalIds || selectedHospitalIds.length === 0) {
            setGlobalMonthList([])
            setLoading(false)
            return
        }

        const hospitalIdsParam = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(',')
            : selectedHospitalIds

        setLoading(true)

        Promise.allSettled(
            TMB_DATE_APIS.map(api =>
                fetch(`${api}/${hospitalIdsParam}`)
                    .then(r => r.ok ? r.json() : null)
                    .catch(() => null)
            )
        ).then(results => {
            let globalMin = Infinity
            let globalMax = -Infinity

            results.forEach(result => {
                if (result.status !== 'fulfilled' || !result.value) return
                const data = result.value
                const rows = data?.response?.result?.rows || []
                const columns = data?.response?.result?.column_order || []
                if (!rows.length || !columns.length) return

                const dateValues = extractDatesFromRows(rows, columns)
                for (const val of dateValues) {
                    if (val < globalMin) globalMin = val
                    if (val > globalMax) globalMax = val
                }
            })

            if (globalMin !== Infinity && globalMax !== -Infinity) {
                const flooredMin = Math.min(globalMin, 202504)
                setGlobalMonthList(buildMonthList(flooredMin, globalMax))
            } else {
                setGlobalMonthList([])
            }
            setLoading(false)
        })
    }, [selectedHospitalIds])

    return (
        <TmbSliderContext.Provider value={{ globalMonthList, loading }}>
            {children}
        </TmbSliderContext.Provider>
    )
}
